import React, { useState, useEffect } from "react";

const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";

function VoiceUsage({ userId }) {
  const [period, setPeriod] = useState("24h");
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [clearing, setClearing] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!userId) return;
    fetchData();
  }, [userId, period]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch aggregated stats
      const statsRes = await fetch(
        `${NEXT_API_URL}/api/voice-usage?user_id=${userId}&period=${period}`
      );
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData.success) {
          setStats(statsData.data);
        }
      }

      // Fetch sessions (get more for pagination)
      const sessionsRes = await fetch(
        `${NEXT_API_URL}/api/voice-usage/sessions?user_id=${userId}&limit=100`
      );
      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        if (sessionsData.success) {
          setSessions(sessionsData.data);
        }
      }
    } catch (err) {
      console.error("Error fetching voice usage:", err);
      setError("Failed to load voice usage data");
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatTotalDuration = (seconds) => {
    if (!seconds) return "0s";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.round(seconds % 60);
    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s`;
    }
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const clearAllData = async () => {
    if (!window.confirm("Are you sure you want to clear all voice usage data? This cannot be undone.")) {
      return;
    }
    setClearing(true);
    try {
      const res = await fetch(
        `${NEXT_API_URL}/api/voice-usage/clear?user_id=${userId}`,
        { method: "DELETE" }
      );
      if (res.ok) {
        setStats(null);
        setSessions([]);
        setCurrentPage(1);
      } else {
        alert("Failed to clear data");
      }
    } catch (err) {
      console.error("Error clearing voice usage:", err);
      alert("Failed to clear data");
    } finally {
      setClearing(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(sessions.length / itemsPerPage);
  const paginatedSessions = sessions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="p-4">
        <div className="d-flex align-items-center justify-content-center" style={{ height: "200px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="alert" style={{
          background: "rgba(231, 0, 11, 0.1)",
          color: "var(--danger)",
          border: "1px solid var(--danger)",
        }}>
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h4 style={{ color: "var(--text)", marginBottom: "24px" }}>
        <i className="bi bi-mic me-2"></i>
        Voice Usage Statistics
      </h4>

      {/* Period Selector */}
      <div className="mb-4">
        <div className="btn-group" role="group">
          {["24h", "7d", "30d", "all"].map((p) => (
            <button
              key={p}
              type="button"
              className={`btn ${period === p ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setPeriod(p)}
              style={{
                background: period === p ? "var(--teal-bg)" : "transparent",
                borderColor: "var(--border-color)",
                color: period === p ? "#fff" : "var(--text)",
              }}
            >
              {p === "24h" ? "24 Hours" : p === "7d" ? "7 Days" : p === "30d" ? "30 Days" : "All Time"}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="card mb-4" style={{
        background: "var(--input-box-bg)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
      }}>
        <div className="card-body">
          <h5 className="card-title mb-3" style={{ color: "var(--text)", fontWeight: 600 }}>
            <i className="bi bi-graph-up me-2"></i>
            Usage Summary
          </h5>
          {stats ? (
            <div className="row g-3">
              <div className="col-md-3 col-6">
                <div className="p-3 rounded" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "12px" }}>
                    Total Responses
                  </div>
                  <div style={{ color: "var(--teal-bg)", fontSize: "24px", fontWeight: 700 }}>
                    {formatNumber(stats.total_responses)}
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="p-3 rounded" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "12px" }}>
                    Total Tokens
                  </div>
                  <div style={{ color: "var(--teal-bg)", fontSize: "24px", fontWeight: 700 }}>
                    {formatNumber(stats.total_tokens)}
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="p-3 rounded" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "12px" }}>
                    Estimated Cost
                  </div>
                  <div style={{ color: "#e74c3c", fontSize: "24px", fontWeight: 700 }}>
                    ${stats.total_cost_dollars}
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="p-3 rounded" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "12px" }}>
                    Cached Tokens
                  </div>
                  <div style={{ color: "#27ae60", fontSize: "24px", fontWeight: 700 }}>
                    {formatNumber(stats.cached_tokens)}
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="p-3 rounded" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "12px" }}>
                    Total Duration
                  </div>
                  <div style={{ color: "#9b59b6", fontSize: "24px", fontWeight: 700 }}>
                    {formatTotalDuration(stats.total_duration_seconds)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p style={{ color: "var(--text)", opacity: 0.6 }}>No usage data available</p>
          )}
        </div>
      </div>

      {/* Token Breakdown */}
      {stats && (
        <div className="card mb-4" style={{
          background: "var(--input-box-bg)",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
        }}>
          <div className="card-body">
            <h5 className="card-title mb-3" style={{ color: "var(--text)", fontWeight: 600 }}>
              <i className="bi bi-pie-chart me-2"></i>
              Token Breakdown
            </h5>
            <div className="row g-3">
              <div className="col-md-2 col-4">
                <div className="p-3 rounded text-center" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "11px" }}>
                    Audio In
                  </div>
                  <div style={{ color: "#3498db", fontSize: "18px", fontWeight: 600 }}>
                    {formatNumber(stats.audio_input_tokens)}
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-4">
                <div className="p-3 rounded text-center" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "11px" }}>
                    Audio Out
                  </div>
                  <div style={{ color: "#9b59b6", fontSize: "18px", fontWeight: 600 }}>
                    {formatNumber(stats.audio_output_tokens)}
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-4">
                <div className="p-3 rounded text-center" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "11px" }}>
                    Text
                  </div>
                  <div style={{ color: "#f39c12", fontSize: "18px", fontWeight: 600 }}>
                    {formatNumber(stats.text_tokens)}
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-4">
                <div className="p-3 rounded text-center" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "11px" }}>
                    Image
                  </div>
                  <div style={{ color: "#e67e22", fontSize: "18px", fontWeight: 600 }}>
                    {formatNumber(stats.image_tokens)}
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-4">
                <div className="p-3 rounded text-center" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "11px" }}>
                    Input
                  </div>
                  <div style={{ color: "var(--text)", fontSize: "18px", fontWeight: 600 }}>
                    {formatNumber(stats.input_tokens)}
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-4">
                <div className="p-3 rounded text-center" style={{ background: "var(--background)" }}>
                  <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "11px" }}>
                    Output
                  </div>
                  <div style={{ color: "var(--text)", fontSize: "18px", fontWeight: 600 }}>
                    {formatNumber(stats.output_tokens)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Sessions */}
      <div className="card" style={{
        background: "var(--input-box-bg)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
      }}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0" style={{ color: "var(--text)", fontWeight: 600 }}>
              <i className="bi bi-clock-history me-2"></i>
              Recent Voice Sessions
            </h5>
            {sessions.length > 0 && (
              <button
                className="btn btn-sm"
                onClick={clearAllData}
                disabled={clearing}
                style={{
                  background: "rgba(231, 76, 60, 0.1)",
                  color: "#e74c3c",
                  border: "1px solid #e74c3c",
                  borderRadius: "6px",
                }}
              >
                {clearing ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <i className="bi bi-trash me-1"></i>
                )}
                Clear All
              </button>
            )}
          </div>
          <div className="table-responsive">
            <table className="table table-sm" style={{ color: "var(--text)", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderColor: "var(--border-color)" }}>
                  <th style={{ color: "var(--text)", opacity: 0.7 }}>Started</th>
                  <th style={{ color: "var(--text)", opacity: 0.7 }}>Responses</th>
                  <th style={{ color: "var(--text)", opacity: 0.7 }}>Total Tokens</th>
                  <th style={{ color: "var(--text)", opacity: 0.7 }}>Audio In</th>
                  <th style={{ color: "var(--text)", opacity: 0.7 }}>Audio Out</th>
                  <th style={{ color: "var(--text)", opacity: 0.7 }}>Cached</th>
                  <th style={{ color: "var(--text)", opacity: 0.7 }}>Duration</th>
                  <th style={{ color: "var(--text)", opacity: 0.7 }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4" style={{ color: "var(--text)", opacity: 0.6 }}>
                      No voice sessions yet
                    </td>
                  </tr>
                ) : (
                  paginatedSessions.map((session) => (
                    <tr key={session.id} style={{ borderColor: "var(--border-color)" }}>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDate(session.startedAt)}</td>
                      <td>{formatNumber(session.totalResponses)}</td>
                      <td>{formatNumber(session.totalTokens)}</td>
                      <td style={{ color: "#3498db" }}>{formatNumber(session.audioInputTokens)}</td>
                      <td style={{ color: "#9b59b6" }}>{formatNumber(session.audioOutputTokens)}</td>
                      <td style={{ color: "#27ae60" }}>{formatNumber(session.cachedTokens)}</td>
                      <td style={{ color: "#2ecc71", fontWeight: 500 }}>{formatDuration(session.durationSeconds)}</td>
                      <td style={{ color: "#e74c3c", fontWeight: 600 }}>${session.costDollars}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3 pt-3" style={{ borderTop: "1px solid var(--border-color)" }}>
              <div style={{ color: "var(--text)", opacity: 0.7, fontSize: "13px" }}>
                Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, sessions.length)} of {sessions.length} sessions
              </div>
              <div className="btn-group" role="group">
                <button
                  className="btn btn-sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    background: "var(--background)",
                    borderColor: "var(--border-color)",
                    color: "var(--text)",
                  }}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  className="btn btn-sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    background: "var(--background)",
                    borderColor: "var(--border-color)",
                    color: "var(--text)",
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <span
                  className="btn btn-sm"
                  style={{
                    background: "var(--teal-bg)",
                    borderColor: "var(--teal-bg)",
                    color: "#fff",
                    cursor: "default",
                  }}
                >
                  {currentPage} / {totalPages}
                </span>
                <button
                  className="btn btn-sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    background: "var(--background)",
                    borderColor: "var(--border-color)",
                    color: "var(--text)",
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  className="btn btn-sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    background: "var(--background)",
                    borderColor: "var(--border-color)",
                    color: "var(--text)",
                  }}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Info */}
      <div className="mt-4 p-3 rounded" style={{
        background: "var(--background)",
        border: "1px solid var(--border-color)",
        fontSize: "12px",
        color: "var(--text)",
        opacity: 0.8,
      }}>
        <i className="bi bi-info-circle me-2"></i>
        <strong>Pricing (gpt-4o-realtime-preview):</strong>{" "}
        Text Input: $5/1M tokens | Audio Input: $100/1M tokens | 
        Text Output: $20/1M tokens | Audio Output: $200/1M tokens | 
        Cached tokens get 50% discount
      </div>
    </div>
  );
}

export default VoiceUsage;
